import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTable from '~/components/CobuTable/CobuTable';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spacer from '~/components/Spacer/Spacer';
import Spinner from '~/components/Spinner/Spinner';
import buttonStyle from '~/components/GatheringsTable/GatheringTable.styles';
import {
  useDeleteGlobalEventTemplate,
  useGlobalEventTemplates
} from '~/hooks/globalEventTemplates/useGlobalEventTemplates';
import { GatheringTemplate } from '~/types/gathering';
import classes from './GlobalEventTemplateManagement.module.css';

const BASE_PATH = '/building/global-event-templates';

const GlobalEventTemplateManagement = () => {
  const history = useHistory();
  const templatesQuery = useGlobalEventTemplates();
  const deleteMutation = useDeleteGlobalEventTemplate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    GatheringTemplate | undefined
  >();
  const [searchValue, setSearchValue] = useState('');

  const filteredTemplates = useMemo(() => {
    const data = templatesQuery.data || [];

    if (!searchValue) {
      return data;
    }

    const term = searchValue.toLowerCase();
    return data.filter(
      (t) =>
        t.title?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.recommendedMonth?.toLowerCase().includes(term)
    );
  }, [templatesQuery.data, searchValue]);

  const openCreate = () => {
    history.push(`${BASE_PATH}/create`);
  };

  const openEdit = (template: GatheringTemplate) => {
    history.push(`${BASE_PATH}/${template.uuid}/edit`);
  };

  const openDelete = (template: GatheringTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!selectedTemplate) return;
    deleteMutation.mutate(selectedTemplate.uuid, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setSelectedTemplate(undefined);
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'GATHERING NAME',
        accessor: (row: GatheringTemplate) => (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {row.title}
          </div>
        )
      },
      {
        Header: 'DESCRIPTION',
        accessor: (row: GatheringTemplate) => (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {row.description}
          </div>
        )
      },
      {
        Header: 'WHAT TO BRING',
        accessor: (row: GatheringTemplate) => (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {row.whatToBring}
          </div>
        )
      },
      {
        Header: 'RECOMMENDED MONTH',
        accessor: (row: GatheringTemplate) => (
          <div>{row.recommendedMonth}</div>
        )
      },
      {
        Header: 'HOW TO',
        accessor: (row: GatheringTemplate) => (
          <div>
            {row.howToUrl && (
              <a href={row.howToUrl} target='_blank' rel='noreferrer'>
                Link
              </a>
            )}
          </div>
        )
      },
      {
        id: 'edit',
        Cell: (cell: CellProps<GatheringTemplate>) => (
          <div
            style={buttonStyle}
            className='pointer'
            onClick={() => openEdit(cell.row.original)}
          >
            EDIT
          </div>
        )
      },
      {
        id: 'delete',
        Cell: (cell: CellProps<GatheringTemplate>) => (
          <div
            style={buttonStyle}
            className='pointer'
            onClick={() => openDelete(cell.row.original)}
          >
            DELETE
          </div>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className='content-container'>
      {deleteMutation.isLoading && <Spinner float />}
      <Breadcrumbs />
      <Spacer height={20} />
      <div className={classes.header}>
        <h1>Global Event Template Management</h1>
        <button
          className='btn-primary btn-standard bold'
          onClick={openCreate}
        >
          Create Template
        </button>
      </div>
      <Spacer height={16} />
      <p className='default-text'>
        Templates created here are available across every building by default.
      </p>
      <Spacer height={16} />
      <input
        type='text'
        placeholder='Search by name, description, or month'
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        className={classes.search}
      />
      <Spacer height={16} />

      {templatesQuery.isLoading && <Spinner />}
      {templatesQuery.isSuccess && (
        <CobuTable data={filteredTemplates} columns={columns} />
      )}

      <GenericModal
        showModal={showDeleteModal}
        title='Delete template?'
        actionText='Delete'
        actionFunction={confirmDelete}
        actionButtonDisabled={deleteMutation.isLoading}
        onChange={(value: boolean) => {
          setShowDeleteModal(value);
          if (!value) setSelectedTemplate(undefined);
        }}
        content={
          <div className='text-align-left'>
            Are you sure you want to delete {selectedTemplate?.title}?
          </div>
        }
      />
    </div>
  );
};

export default GlobalEventTemplateManagement;
